export async function getLatLngFromAddress(address) {
  return new Promise((resolve, reject) => {
    (async () => {
      const encodedAddress = encodeURIComponent(address);
      const key = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY;
      const requestURL = `https://maps.googleapis.com/maps/api/geocode/json?address=1${encodedAddress}&key=${key}`;
      try {
        const response = await fetch(requestURL);
        const data = await response.json();
        const latLng = {
          lat: data.results[0].geometry.location.lat,
          lng: data.results[0].geometry.location.lng,
        };
        resolve(latLng);
      } catch (err) {
        reject(err);
      }
    })();
  });
}

export async function getCenter(campusCenter, address) {
  let center = null;

  // First, use the campus center entered in the CMS if available.
  // campusCenter is pre-validated in API - null if lat/lng missing.
  if (campusCenter) {
    center = campusCenter;
  }

  // Next, try getting the center from the address.
  else if (address) {
    try {
      center = await getLatLngFromAddress(address);
    } catch (err) {
      console.error('Failed to get lat lng from address', err);
    }
  }

  // Default to Winston-Salem, NC.
  else {
    center = {
      lat: 36.115,
      lng: -80.259,
    };
  }
  return center;
}

/**
 * Determines the active center based on SiteSee configuration
 * siteSeeCenter is pre-validated in API - null if lat/lng missing
 * @param {boolean} isSiteSee - Whether this is a SiteSee instance
 * @param {Object|null} siteSeeCenter - SiteSee center coordinates (null if invalid)
 * @param {Object} commPinLatLng - Community pin coordinates
 * @returns {Object} The active center coordinates
 */
export function getActiveCenter(isSiteSee, siteSeeCenter, commPinLatLng) {
  return (isSiteSee && siteSeeCenter) ? siteSeeCenter : commPinLatLng;
}

/**
 * Calculates the initial zoom based on configuration
 * @param {boolean} isSiteSee - Whether this is a SiteSee instance
 * @param {number} siteSeeInitialZoom - SiteSee initial zoom
 * @param {Object} zooms - Zoom configuration object
 * @param {number} mapZoomToEnableLevels - Default zoom level
 * @returns {number} The initial zoom level
 */
export function getInitialZoom(isSiteSee, siteSeeInitialZoom, zooms, mapZoomToEnableLevels) {
  const nonSiteSeeInitialZoom = zooms?.initial || mapZoomToEnableLevels;
  return isSiteSee ? siteSeeInitialZoom : nonSiteSeeInitialZoom;
}

export function getLatLngFromFocusXYZ(focusXYZ, svgData, overlayBounds) {
  const { x, y } = focusXYZ;

  // The SVG fills the overlay div via CSS (width/height: 100%), so SVG coords
  // map to the overlay area through the viewBox — not the width/height attrs.
  const attrs = svgData?.attributes_array;
  let vb = attrs?.parsed_view_box;
  if ( !vb && attrs?.viewBox ) {
    const [ vbX, vbY, vbWidth, vbHeight ] = attrs.viewBox.split( ' ' ).map( Number );
    vb = { x: vbX || 0, y: vbY || 0, width: vbWidth, height: vbHeight };
  }

  const lngRatio = ( x - vb.x ) / vb.width;
  const latRatio = ( y - vb.y ) / vb.height;

  const sw = overlayBounds.getSouthWest() || {};
  const ne = overlayBounds.getNorthEast() || {};
  const swLng = sw.lng();
  const swLat = sw.lat();
  const neLng = ne.lng();
  const neLat = ne.lat();

  const lngWidth = neLng - swLng;
  const latHeight = neLat - swLat;

  // I know this can be done with just sw and ne but my brain can't grasp
  // those calculations.
  const nwLat = neLat;
  const nwLng = swLng;

  const lat = nwLat - (latHeight * latRatio);
  const lng = nwLng + (lngWidth * lngRatio);

  const location = new window.google.maps.LatLng(lat, lng);
  return location;
}

/**
 * Animates map navigation with deliberate timing between pan and zoom steps.
 * Pans first, waits for the pan to settle, then zooms incrementally
 * one level at a time with delays between each step.
 *
 * @param {Object} map - Google Maps instance
 * @param {Object} targetLatLng - Target LatLng to pan to
 * @param {number} targetZoom - Desired final zoom level
 * @param {Object} options
 * @param {Function} options.onZoomStep - Called before each zoom step (to flag programmatic zoom)
 * @param {Function} options.onComplete - Called when animation finishes
 * @returns {Function} cleanup function to cancel pending timeouts
 */
export function animateMapNavigation(map, targetLatLng, targetZoom, {
  onZoomStep,
  onComplete,
} = {}) {
  const PAN_SETTLE_DELAY = 400;
  const ZOOM_STEP_DELAY = 150;
  // When the zoom gap is large, skip intermediate levels and only
  // animate the last few steps so the transition feels snappy.
  const MAX_ANIMATED_STEPS = 3;
  const timers = [];

  const cleanup = () => {
    timers.forEach((id) => clearTimeout(id));
    timers.length = 0;
  };

  // Step 1: Pan to target using Google's native smooth pan
  map.panTo(targetLatLng);

  // Step 2: After pan settles, zoom to target
  const panTimer = setTimeout(() => {
    const currentZoom = map.getZoom();
    if (!targetZoom || currentZoom === targetZoom) {
      if (onComplete) onComplete(targetZoom);
      return;
    }

    const totalSteps = Math.abs(targetZoom - currentZoom);
    const direction = targetZoom > currentZoom ? 1 : -1;

    // Jump past intermediate levels, then animate the final steps.
    const skipSteps = Math.max(0, totalSteps - MAX_ANIMATED_STEPS);
    if (skipSteps > 0) {
      if (onZoomStep) onZoomStep();
      map.setZoom(currentZoom + (direction * skipSteps));
    }

    const animatedSteps = totalSteps - skipSteps;
    for (let i = 1; i <= animatedSteps; i++) {
      const stepTimer = setTimeout(() => {
        if (onZoomStep) onZoomStep();
        map.setZoom(currentZoom + (direction * (skipSteps + i)));
        if (i === animatedSteps && onComplete) {
          onComplete(targetZoom);
        }
      }, ZOOM_STEP_DELAY * i);
      timers.push(stepTimer);
    }

    if (animatedSteps === 0 && onComplete) {
      onComplete(targetZoom);
    }
  }, PAN_SETTLE_DELAY);
  timers.push(panTimer);

  return cleanup;
}
